'use client';
import { useState, useContext } from 'react';

import { logoutAuthz } from '@storefront/actions/logoutAuthz';

import SignOutContext from '@storefront/context/SignOutContext';
import DataCollection from '@storefront/services/DataCollection';
import { uiEvents } from '@storefront/services/DataCollection/simpleUiEvents';
import {
  getAemCallbackURL,
  getCurrentProductConfigurationURL,
} from '@storefront/services/SessionHelperService/sessionHelperService';
import { SimpleUiEvent } from '@storefront/types/webanalytics';
import { Button, Modal } from '@veneer/core';
import { useSession } from 'next-auth/react';
import { useTranslation } from 'react-i18next';
import styles from './modal-sign-out.module.scss';
import { BASE_URL } from '@storefront/utils/constants';
import { signOutHandler } from '@storefront/app/api/auth/[...nextauth]/auth.client.utils';

import ThemeProviderWrapperV2 from '@storefront/app/ThemeProviderWrapperV2';

export interface ModalSignOutProps {
  modalName?: string;
  setShowModal: (show: boolean) => void;
  showModal: boolean;
}

type Step = 'checkoutOnePage' | 'alreadyHaveInstantInk' | 'orderConfirmation';

const getCallBackURL = () => {
  const basePath = BASE_URL;

  const pdpConfigUrlWithBasePath =
    getCurrentProductConfigurationURL().startsWith(basePath)
      ? getCurrentProductConfigurationURL()
      : basePath.concat(getCurrentProductConfigurationURL());

  const page = getCurrentProductConfigurationURL()
    ? pdpConfigUrlWithBasePath
    : `${getAemCallbackURL()}`;
  return page;
};

const ModalSignOut = (props: ModalSignOutProps) => {
  const { modalName, setShowModal, showModal } = props;
  const { currentStep } = useContext(SignOutContext);
  const { t } = useTranslation();
  const { data } = useSession();
  const id_token = data?.id_token;
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const stepToscreenName = {
    checkoutOnePage: 'CheckoutOnePage',
    alreadyHaveInstantInk: 'AlreadyHaveInstantInk',
    orderConfirmation: 'OrderConfirmation',
  };
  const handleConfirm = async () => {
    setIsLoading(true);
    await sendAnalyticsModalSignOut({
      ...uiEvents.signOutGenericBC,
      screenName: stepToscreenName[currentStep as Step],
    });
    const callbackURL = getCallBackURL();
    await logoutAuthz(id_token, callbackURL);
    signOutHandler({ redirect: true, path: callbackURL });
  };

  const handleCancel = () => {
    setShowModal(false);
  };

  const sendAnalyticsModalSignOut = async (event: SimpleUiEvent) => {
    await DataCollection.sendEvent(event);
  };

  return (
    <ThemeProviderWrapperV2>
      <Modal
        id="modal-signout"
        closeButton={!isLoading}
        show={showModal}
        expanded
        maxWidth={650}
        data-testid="ModalSignOut"
        onClose={() => setShowModal(false)}
        className={styles['modal-signout-container']}
      >
        <div className={styles['container']}>
          <p className={`${styles['title']} title-small`}>
            {t('signOut.modalTitle')}
          </p>
          <p className={`${styles['text-center']} body-large`}>
            {t('signOut.modalBody')}
          </p>

          <div className={styles['buttons-container']}>
            <Button
              tabIndex={0}
              disabled={isLoading}
              id={'cancel-signout-button'}
              data-testid={'cancel-signout-button'}
              onClick={handleCancel}
            >
              {t('signOut.cancelAction')}
            </Button>
            <ThemeProviderWrapperV2 theme={'secondaryThemeV2'}>
              <Button
                tabIndex={0}
                id="confirm-signout-button"
                data-testid={'confirm-signout-button'}
                onClick={handleConfirm}
                loading={isLoading}
                disabled={isLoading}
                appearance="secondary"
              >
                {t('signOut.confirmAction')}
              </Button>
            </ThemeProviderWrapperV2>
          </div>
        </div>
      </Modal>
      <span>{modalName}</span>
    </ThemeProviderWrapperV2>
  );
};

export default ModalSignOut;
